import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import HeroSection from '../components/HeroSection';
import BenefitCard from '../components/BenefitCard';
import CaseCard from '../components/CaseCard';
import TestimonialCard from '../components/TestimonialCard';
import FAQAccordion from '../components/FAQAccordion';
import DemoForm from '../components/DemoForm';
import { Zap, Users, TrendingUp, X } from 'lucide-react';

const CRMPage = () => {
  const { country } = useParams();
  const [showDemoForm, setShowDemoForm] = useState(false);

  const benefits = [
    {
      icon: Zap,
      title: 'Automação Inteligente de Processos',
      description: 'Elimine tarefas repetitivas e manuais. O CRM Kommo automatiza fluxos de trabalho, liberando sua equipe para atividades estratégicas que geram mais valor.'
    },
    {
      icon: Users,
      title: 'Gestão Centralizada de Clientes',
      description: 'Todas as informações de seus clientes em um único lugar. Histórico de interações, documentos, negócios em andamento - tudo acessível para toda a equipe.'
    },
    {
      icon: TrendingUp,
      title: 'Análise de Dados em Tempo Real',
      description: 'Dashboards intuitivos e relatórios precisos. Monitore o desempenho, identifique oportunidades e tome decisões baseadas em dados concretos.'
    }
  ];

  const cases = [
    {
      title: 'Aumentando Produtividade em 40% com CRM Kommo',
      company: 'Empresa do Agronegócio',
      challenge: 'A empresa enfrentava desorganização no acompanhamento de clientes. Informações espalhadas em e-mails, planilhas e diferentes plataformas.',
      solution: 'Implementamos o CRM Kommo com fluxos de trabalho customizados e integramos com ferramentas existentes.',
      result: '40% aumento em produtividade | 25% redução em tempo de ciclo de vendas'
    },
    {
      title: 'Automação de Processos Reduziu Custos em 30%',
      company: 'Empresa de Tecnologia',
      challenge: 'Processos manuais consumiam 20 horas por semana da equipe. Erros frequentes em entrada de dados.',
      solution: 'Customizamos o CRM Kommo com automações inteligentes que eliminam tarefas repetitivas.',
      result: '30% redução em custos operacionais | 20 horas/semana liberadas'
    }
  ];

  const testimonials = [
    {
      name: 'João Silva',
      position: 'Gerente de Vendas',
      company: 'Agro Tech Brasil',
      testimonial: 'O CRM Kommo transformou a forma como trabalhamos. A implementação foi suave, o treinamento excelente, e o suporte foi fundamental. Nossa equipe é muito mais produtiva e organizada!'
    },
    {
      name: 'Maria Santos',
      position: 'Diretora de Operações',
      company: 'Global Solutions',
      testimonial: 'Antes do CRM Kommo, tínhamos informações espalhadas. Agora, tudo está centralizado e automatizado. O ROI foi alcançado em menos de 3 meses.'
    }
  ];

  const faqs = [
    {
      question: 'O que é CRM Kommo e como funciona?',
      answer: 'CRM Kommo é um sistema de gestão de relacionamento com clientes que centraliza todas as informações em um único lugar. Ele permite automação de processos, acompanhamento de negócios em tempo real e análise de dados.'
    },
    {
      question: 'Quanto tempo leva para implementar o CRM Kommo?',
      answer: 'A implementação varia conforme a complexidade do seu negócio, mas geralmente leva entre 2 a 4 semanas. Oferecemos 60 dias de acompanhamento dedicado.'
    },
    {
      question: 'Posso integrar o CRM Kommo com outras ferramentas?',
      answer: 'Sim! O CRM Kommo oferece integrações com centenas de ferramentas populares. Nossa equipe pode ajudar a configurar essas integrações durante a implementação.'
    },
    {
      question: 'Vocês oferecem suporte após a implementação?',
      answer: 'Sim! Oferecemos 60 dias de acompanhamento dedicado após a implementação, com suporte técnico e consultoria.'
    }
  ];

  return (
    <div className="min-h-screen bg-aug-cream">
      <Header />
      <WhatsAppButton />
      
      <div className="pt-20">
        <HeroSection
          title="CRM Kommo - Gestão Inteligente de Relacionamento"
          subtitle="Centralize dados, automatize processos e cresça com inteligência"
          description="O CRM Kommo é a solução completa para gestão de relacionamento com clientes. Centralize informações, automatize fluxos e tome decisões baseadas em dados precisos."
          ctaText="Solicitar Demonstração"
          ctaAction={() => setShowDemoForm(true)}
        />

        {/* Benefits */}
        <section className="py-4xl bg-white">
          <div className="max-w-[1400px] mx-auto px-xl">
            <h2 className="text-4xl font-bold text-aug-gray-dark mb-3xl text-center">Benefícios</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <BenefitCard key={index} {...benefit} />
              ))}
            </div>
          </div>
        </section>

        {/* Cases */}
        <section className="py-4xl bg-aug-cream">
          <div className="max-w-[1400px] mx-auto px-xl">
            <h2 className="text-4xl font-bold text-aug-gray-dark mb-3xl text-center">Casos de Sucesso</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {cases.map((caseItem, index) => (
                <CaseCard key={index} {...caseItem} />
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-4xl bg-white">
          <div className="max-w-[1400px] mx-auto px-xl">
            <h2 className="text-4xl font-bold text-aug-gray-dark mb-3xl text-center">Depoimentos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((testimonial, index) => (
                <TestimonialCard key={index} {...testimonial} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-4xl bg-aug-cream">
          <div className="max-w-[1400px] mx-auto px-xl">
            <h2 className="text-4xl font-bold text-aug-gray-dark mb-3xl text-center">Perguntas Frequentes</h2>
            <div className="max-w-3xl mx-auto">
              <FAQAccordion faqs={faqs} />
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-4xl bg-gradient-to-r from-aug-blue to-aug-green text-white">
          <div className="max-w-[1400px] mx-auto px-xl text-center">
            <h2 className="text-4xl font-bold mb-4">Pronto para transformar seu negócio?</h2>
            <p className="text-xl mb-8">A Agro Urban Global está aqui para ajudar você a implementar o CRM Kommo</p>
            <button
              onClick={() => setShowDemoForm(true)}
              className="bg-white text-aug-blue px-8 py-4 rounded-xl font-semibold text-lg hover:bg-aug-cream transition-all duration-300"
              data-testid="crm-cta-button"
            >
              Comece Agora
            </button>
          </div>
        </section>
      </div>

      {/* Demo Form Modal */}
      {showDemoForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 relative">
            <button
              onClick={() => setShowDemoForm(false)}
              className="absolute top-4 right-4 text-aug-gray-dark hover:text-aug-error"
              data-testid="close-demo-form"
            >
              <X className="w-6 h-6" />
            </button>
            <h3 className="text-2xl font-bold text-aug-gray-dark mb-6">Solicitar Demonstração</h3>
            <DemoForm country={country} onClose={() => setShowDemoForm(false)} />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CRMPage;